import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendWelcomeEmail } from '../../services/emailService';

const AuthConfirm = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [isVendor, setIsVendor] = useState(false);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // Supabase appends token_hash + type as query params on email confirm links
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params?.get('token_hash');
      const type = params?.get('type');

      // Also handle the older #access_token fragment style
      const hash = window.location?.hash;
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      const accessToken = hashParams?.get('access_token');
      const refreshToken = hashParams?.get('refresh_token');

      try {
        if (tokenHash && type) {
          // PKCE / OTP style (Supabase v2 default)
          const { error } = await supabase?.auth?.verifyOtp({ token_hash: tokenHash, type });
          if (error) {
            setErrorMessage(error?.message || 'Verification failed. The link may have expired.');
            setStatus('error');
          } else {
            // Get session to retrieve user info for welcome email
            const { data: sessionData } = await supabase?.auth?.getSession();
            const user = sessionData?.session?.user;
            if (user?.email) {
              const fullName = user?.user_metadata?.full_name || '';
              const firstName = fullName?.split(' ')?.[0] || user?.email?.split('@')?.[0] || '';
              try {
                await sendWelcomeEmail(user?.email, { name: firstName });
              } catch (_) {
                // Silent — welcome email failure must never affect verification UX
              }
            }
            // Check if the confirmed user is a vendor
            if (user?.email) {
              const { data: vendorData } = await supabase?.from('vendors')?.select('id')?.eq('email', user?.email)?.maybeSingle();
              setIsVendor(!!vendorData);
            }
            setStatus('success');
          }
        } else if (accessToken) {
          // Legacy implicit flow
          const { error } = await supabase?.auth?.setSession({ access_token: accessToken, refresh_token: refreshToken });
          if (error) {
            setErrorMessage(error?.message || 'Session error. Please try signing in.');
            setStatus('error');
          } else {
            // Get session to retrieve user info for welcome email
            const { data: sessionData } = await supabase?.auth?.getSession();
            const user = sessionData?.session?.user;
            if (user?.email) {
              const fullName = user?.user_metadata?.full_name || '';
              const firstName = fullName?.split(' ')?.[0] || user?.email?.split('@')?.[0] || '';
              try {
                await sendWelcomeEmail(user?.email, { name: firstName });
              } catch (_) {
                // Silent
              }
            }
            // Check if the confirmed user is a vendor
            if (user?.email) {
              const { data: vendorData } = await supabase?.from('vendors')?.select('id')?.eq('email', user?.email)?.maybeSingle();
              setIsVendor(!!vendorData);
            }
            setStatus('success');
          }
        } else {
          // No tokens found — maybe user landed here directly
          setStatus('success');
        }
      } catch (err) {
        setErrorMessage('Something went wrong. Please try again.');
        setStatus('error');
      }
    };

    handleEmailConfirmation();
  }, []);

  const handleContinue = () => {
    if (isVendor) {
      navigate('/vendor-hub-dashboard');
    } else {
      navigate('/all-projects');
    }
  };

  const handleRetry = () => {
    navigate('/login-sign-up');
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}
    >
      {/* Logo */}
      <div className="mb-10">
        <img
          src="/assets/images/Stackd_logo_design_no_background_white_text-1772559212336.png"
          alt="Stackd logo"
          className="h-12 object-contain"
        />
      </div>
      {/* Card */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-10 text-center">
        {status === 'verifying' && (
          <>
            <div className="flex justify-center mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}
              >
                <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Verifying your email…</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Just a moment while we confirm your account.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}
              >
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Email Confirmed! 🎉</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
              Your Stackd account is verified and ready to go. Start building your first stack!
            </p>
            <button
              onClick={handleContinue}
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
            >
              {isVendor ? 'Go to Vendor Dashboard' : 'Go to My Projects'}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Verification Failed</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">{errorMessage}</p>
            <p className="text-gray-400 text-xs mb-8">The confirmation link may have expired. Please sign in to request a new one.</p>
            <button
              onClick={handleRetry}
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
            >
              Back to Sign In
            </button>
          </>
        )}
      </div>
      {/* Footer note */}
      <p className="mt-8 text-white/40 text-xs text-center">
        © {new Date()?.getFullYear()} Stackd. All rights reserved.
      </p>
    </div>
  );
};

export default AuthConfirm;
